import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Download, Upload, X, Search, MapPin, Briefcase, ChevronDown, ChevronUp, XCircle, CheckCircle, User, SlidersHorizontal, Calendar } from 'lucide-react';
import { traineeRegistrationApi, TraineesRegisteredData, ValidRegistrationMobilisation } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

// Validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const SUPPORTED_FORMATS = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

type FormValues = {
    mobilisation_id: number;
    file: FileList;
};

// Component for a single Mobilisation card
interface MobilisationCardProps {
    mobilisation: TraineesRegisteredData;
    onClick: (mobilisation: TraineesRegisteredData) => void;
    userRole: string | undefined;
}

const MobilisationCard: React.FC<MobilisationCardProps> = ({ mobilisation, onClick, userRole }) => {
    // Calculate the number of total registered, consented, and final trainees
    const totalRegisteredCount = mobilisation.trainee_registrations.length;
    const consentedCount = mobilisation.trainee_registrations.filter(reg => reg.consent_given).length;
    const finalTraineesCount = mobilisation.trainee_registrations.filter(reg => reg.consent_mail_sent).length;

    // Helper to format dates
    const formatDate = (dateString: string | number | Date | undefined) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };
    const formatDateTimeIST = (dateString: string | undefined | null) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'N/A';
        const formatted = new Intl.DateTimeFormat('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        }).format(date);
        return `${formatted} IST`;
    };

    return (
        <div
            className="bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1 flex flex-col justify-between"
            onClick={() => onClick(mobilisation)}
        >
            <div>
                <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 bg-blue-100 p-3 rounded-full">
                        <Briefcase className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                        <h3 className="text-xl font-bold text-gray-900 leading-tight">{mobilisation.title}</h3>
                        <p className="text-sm text-gray-500">{mobilisation.program.name}</p>
                    </div>
                </div>
                <div className="space-y-3 text-sm text-gray-600 mb-4">
                    <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Admin: <span className="font-semibold">{mobilisation.center_admin.full_name}</span></span>
                    </div>
                    <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{mobilisation.city}, {mobilisation.state}</span>
                    </div>
                    {userRole === 'SUPER_ADMIN' && mobilisation.mobilisation_partner && (
                        <div className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
                            <span>Partner: <span className="font-semibold">{mobilisation.mobilisation_partner.name}</span></span>
                        </div>
                    )}
                    {/* New: Registration Dates */}
                    <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <span>
                            Registration: <span className="font-semibold">{formatDate(mobilisation.registration_start_date)} - {formatDate(mobilisation.registration_end_date)}</span>
                        </span>
                    </div>
                    {(mobilisation.effective_registration_end_at || mobilisation.effective_registration_end_display) && (
                        <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                            <span>
                                Effective End Date: <span className="font-semibold">
                                    {mobilisation.effective_registration_end_display || formatDateTimeIST(mobilisation.effective_registration_end_at)}
                                </span>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Trainee Counts Section - Improved visual style */}
            <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                        <div className="text-2xl font-bold text-blue-600">{totalRegisteredCount}</div>
                        <div className="text-xs font-medium text-gray-500 mt-1">Registered</div>
                    </div>
                      <div className="border-l border-gray-200">
                        <div className="text-2xl font-bold text-purple-600">{finalTraineesCount}</div>
                        <div className="text-xs font-medium text-gray-500 mt-1">Final Selected</div>
                    </div>
                    <div className="border-l border-gray-200">
                        <div className="text-2xl font-bold text-green-600">{consentedCount}</div>
                        <div className="text-xs font-medium text-gray-500 mt-1">Consented</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal component to show details for a specific mobilisation
interface MobilisationDetailsModalProps {
    mobilisation: TraineesRegisteredData;
    onClose: () => void;
}

const MobilisationDetailsModal: React.FC<MobilisationDetailsModalProps> = ({ mobilisation, onClose }) => {
    const { user } = useAuth();
    const [traineeSearchTerm, setTraineeSearchTerm] = useState<string>('');
    const [traineeCurrentPage, setTraineeCurrentPage] = useState<number>(1);
    const traineeItemsPerPage = 10;
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);

    // List of all available columns
    const allColumns = useMemo(() => [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'consent', label: 'Consent' },
        { key: 'mail_sent', label: 'Consent Mail Sent' },
        { key: 'registered_date', label: 'Registration Date' },
        { key: 'source', label: 'Source' },
    ], []);

    // State for visible columns, all are visible by default
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => new Set(allColumns.map(c => c.key)));

    const handleColumnToggle = (key: string) => {
        setVisibleColumns(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    // Filter and paginate trainees within an expanded mobilisation
    const getFilteredAndPaginatedTrainees = useMemo(() => {
        const filteredTrainees = mobilisation.trainee_registrations.filter(reg =>
            traineeSearchTerm === '' ||
            reg.trainee.user.full_name.toLowerCase().includes(traineeSearchTerm.toLowerCase()) ||
            reg.trainee.user.email.toLowerCase().includes(traineeSearchTerm.toLowerCase()) ||
            reg.trainee.user.phone.toLowerCase().includes(traineeSearchTerm.toLowerCase())
        );

        const startIndex = (traineeCurrentPage - 1) * traineeItemsPerPage;
        const endIndex = startIndex + traineeItemsPerPage;
        return filteredTrainees.slice(startIndex, endIndex);
    }, [mobilisation.trainee_registrations, traineeSearchTerm, traineeCurrentPage, traineeItemsPerPage]);

    const getTotalTraineePages = useMemo(() => {
        const filteredTrainees = mobilisation.trainee_registrations.filter(reg =>
            traineeSearchTerm === '' ||
            reg.trainee.user.full_name.toLowerCase().includes(traineeSearchTerm.toLowerCase()) ||
            reg.trainee.user.email.toLowerCase().includes(traineeSearchTerm.toLowerCase()) ||
            reg.trainee.user.phone.toLowerCase().includes(traineeSearchTerm.toLowerCase())
        );
        return Math.ceil(filteredTrainees.length / traineeItemsPerPage);
    }, [mobilisation.trainee_registrations, traineeSearchTerm, traineeItemsPerPage]);

    const handleTraineePageChange = (page: number) => {
        setTraineeCurrentPage(page);
    };

    return (
        <ModalPortal>
            <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 relative h-full max-h-[90vh] flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-4">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900">{mobilisation.title}</h3>
                        <p className="text-sm text-gray-500">
                            {mobilisation.program.name} in {mobilisation.city}, {mobilisation.state}
                            {user?.role === 'SUPER_ADMIN' && mobilisation.mobilisation_partner && (
                                <span className="ml-2"> | Partner: {mobilisation.mobilisation_partner.name}</span>
                            )}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Trainee Actions and Search */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search trainees by name, email, or phone..."
                            className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-blue-500"
                            value={traineeSearchTerm}
                            onChange={(e) => {
                                setTraineeSearchTerm(e.target.value);
                                setTraineeCurrentPage(1);
                            }}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 relative">
                        <div className="relative inline-block text-left">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                            >
                                <SlidersHorizontal className="w-4 h-4 mr-2" />
                                Columns
                                {showColumnDropdown ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
                            </button>
                            {showColumnDropdown && (
                                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                                    <div className="py-1">
                                        {allColumns.map(column => (
                                            <label key={column.key} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns.has(column.key)}
                                                    onChange={() => handleColumnToggle(column.key)}
                                                    className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                                />
                                                <span className="ml-2">{column.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Trainee List Table */}
                <div className="flex-grow overflow-y-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                {visibleColumns.has('name') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>}
                                {visibleColumns.has('email') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>}
                                {visibleColumns.has('phone') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>}
                                {visibleColumns.has('consent') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consent</th>}
                                {visibleColumns.has('mail_sent') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consent Mail Sent</th>}
                                {visibleColumns.has('registered_date') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registration Date</th>}
                                {visibleColumns.has('source') && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {getFilteredAndPaginatedTrainees.length > 0 ? (
                                getFilteredAndPaginatedTrainees.map((reg) => (
                                    <tr key={reg.registration_id}>
                                        {visibleColumns.has('name') && <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{reg.trainee.user.full_name}</td>}
                                        {visibleColumns.has('email') && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reg.trainee.user.email}</td>}
                                        {visibleColumns.has('phone') && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reg.trainee.user.phone}</td>}
                                        {visibleColumns.has('consent') && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reg.consent_given ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {reg.consent_timestamp ? new Date(reg.consent_timestamp).toLocaleDateString() : 'No'}
                                                </span>
                                            </td>
                                        )}
                                        {visibleColumns.has('mail_sent') && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reg.consent_mail_sent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {reg.consent_mail_sent ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                        )}
                                        {visibleColumns.has('registered_date') && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(reg.registration_date).toLocaleDateString()}</td>}
                                        {visibleColumns.has('source') && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{reg.source || 'N/A'}</td>}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={allColumns.length} className="px-6 py-4 text-center text-gray-500">
                                        No trainees found matching your search.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Trainee Pagination Controls */}
                {getTotalTraineePages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <button
                            onClick={() => handleTraineePageChange(traineeCurrentPage - 1)}
                            disabled={traineeCurrentPage === 1}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">
                            Page <span className="font-semibold">{traineeCurrentPage}</span> of <span className="font-semibold">{getTotalTraineePages}</span>
                        </span>
                        <button
                            onClick={() => handleTraineePageChange(traineeCurrentPage + 1)}
                            disabled={traineeCurrentPage === getTotalTraineePages}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
                </div>
            </div>
        </ModalPortal>
    );
};


// New modal for uploading trainees
interface UploadTraineesModalProps {
    onClose: () => void;
    onSubmit: (data: FormValues) => Promise<void>;
    openMobilisations: ValidRegistrationMobilisation[];
    uploading: boolean;
}

const UploadTraineesModal: React.FC<UploadTraineesModalProps> = ({ onClose, onSubmit, openMobilisations, uploading }) => {
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormValues>();
    const selectedMobilisationId = watch('mobilisation_id');
    const selectedMobilisation = useMemo(
        () => openMobilisations.find((mob) => mob.mobilisation_id === Number(selectedMobilisationId)),
        [openMobilisations, selectedMobilisationId]
    );
    const formatIstDateTime = useCallback((isoString?: string) => {
        if (!isoString) return 'N/A';
        const value = new Date(isoString);
        if (Number.isNaN(value.getTime())) return 'N/A';
        const formatted = new Intl.DateTimeFormat('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        }).format(value);
        return `${formatted} IST`;
    }, []);

    return (
        <ModalPortal>
            <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
                {/* Modal Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Upload Trainees</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-6"
                >
                    {/* Mobilisation Select */}
                    <div>
                        <label htmlFor="mobilisation-select" className="block text-sm font-medium text-gray-700">
                            Select Mobilisation
                        </label>
                        <select
                            id="mobilisation-select"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base"
                            {...register('mobilisation_id', {
                                required: 'Please select a mobilisation.',
                                valueAsNumber: true,
                            })}
                        >
                            <option value="">Choose a mobilisation...</option>
                            {openMobilisations.map((mob) => (
                                <option key={mob.mobilisation_id} value={mob.mobilisation_id}>
                                    {mob.title}
                                </option>
                            ))}
                        </select>
                        {openMobilisations.length === 0 && (
                            <p className="mt-1 text-sm text-amber-700">
                                No mobilisation is open for trainee upload right now.
                            </p>
                        )}
                        {errors.mobilisation_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.mobilisation_id.message}</p>
                        )}
                        {selectedMobilisation && (
                            <p className="mt-2 text-sm text-gray-700">
                                Effective End Date* :{' '}
                                <span className="font-semibold">
                                    {selectedMobilisation.effective_registration_end_display
                                        || formatIstDateTime(selectedMobilisation.effective_registration_end_at)}
                                </span>
                            </p>
                        )}
                        <p className="mt-2 text-xs text-gray-500">
                            * Registration closes at 23:59:59 IST on the last Launchpad day, or the registration window end date — whichever comes first.
                        </p>
                    </div>

                    {/* File Upload */}
                    <div>
                        <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700">
                            Upload Trainee Data File (.xlsx)
                        </label>
                        <input
                            id="file-upload"
                            type="file"
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            {...register('file', {
                                required: 'File is required.',
                                validate: (value) => {
                                    const file = value?.[0];
                                    if (!file) return 'File is required.';
                                    if (file.size > MAX_FILE_SIZE) return `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
                                    if (!SUPPORTED_FORMATS.includes(file.type)) return 'Only .xlsx files are supported.';
                                    return true;
                                },
                            })}
                        />
                        {errors.file && (
                            <p className="mt-1 text-sm text-red-600">{errors.file.message}</p>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={uploading || openMobilisations.length === 0}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                        >
                            {uploading ? (
                                <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                            ) : (
                                <Upload className="mr-2 h-4 w-4" />
                            )}
                            Upload
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </ModalPortal>
    );
};


const TraineesRegistration: React.FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [traineeMobilisations, setTraineeMobilisations] = useState<TraineesRegisteredData[]>([]);
    const [selectedMobilisation, setSelectedMobilisation] = useState<TraineesRegisteredData | null>(null);
    const [mobilisationSearchTerm, setMobilisationSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6; // Number of cards to display per page

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [openMobilisations, setOpenMobilisations] = useState<ValidRegistrationMobilisation[]>([]);

    const isMPAdminOrCAAdmin = user?.role === 'CENTER_ADMIN' || user?.role === 'PROJECT_MANAGER' || user?.role === 'MOBILISATION_PARTNER_ADMIN';

    const fetchTraineeRegistrations = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await traineeRegistrationApi.getTraineeList();
            if (res.success && res.data) {
                setTraineeMobilisations(res.data);
            }
        } catch (err: any) {
            console.error('Failed to fetch trainee list:', err);
            setError('Unable to load trainee registrations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchOpenMobilisations = useCallback(async () => {
        try {
            const res = await traineeRegistrationApi.getValidMobilisations();
            if (res.success && res.data) {
                setOpenMobilisations(res.data);
            }
        } catch {
            setError('Unable to load mobilisations for upload. Please refresh.');
        }
    }, []);

    const downloadTemplate = useCallback(async () => {
        setError(null);
        setSuccess(null);
        setDownloadLoading(true);
        try {
            const blob = await traineeRegistrationApi.getTemplate();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Trainees_Template.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setSuccess('Template downloaded successfully.');
        } catch {
            setError('Unable to download template. Please try again.');
        } finally {
            setDownloadLoading(false);
        }
    }, []);

    const onSubmit = async (data: FormValues) => {
        setError(null);
        setSuccess(null);
        setUploading(true);
        try {
            const response = await traineeRegistrationApi.bulkCreateTrainees({
                mobilisation_id: data.mobilisation_id,
                file: data.file[0],
            });

            // Handle different response types (JSON for errors, file for success)
            const contentType = response.headers['content-type'] || '';
            if (contentType.includes('application/json')) {
                const text = await new Response(response.data).text();
                const payload = JSON.parse(text);
                if (payload.success) {
                    setSuccess(payload.message);
                    fetchTraineeRegistrations(); // Trigger a re-fetch of data
                } else {
                    setError(`Upload failed: ${payload.message}`);
                }
            } else {
                const blob = new Blob([response.data], {
                    type: contentType
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'error-report.xlsx';
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                const v = response.headers['x-valid-count'];
                const i = response.headers['x-invalid-count'];
                setSuccess(`${v} trainees have been successfully registered. Errors in ${i}.`);
                fetchTraineeRegistrations(); // Refresh the list after successful upload
            }

        } catch (error: any) {
            setError(`Upload failed: ${error.message}`);
        } finally {
            setUploading(false);
            setShowUploadModal(false); // Close modal on completion
        }
    };


    useEffect(() => {
        fetchTraineeRegistrations();
        if (isMPAdminOrCAAdmin) {
            fetchOpenMobilisations();
        }
    }, [user, isMPAdminOrCAAdmin, fetchOpenMobilisations]);

    const filteredAndPaginatedMobilisations = useMemo(() => {
        const filtered = traineeMobilisations.filter(mob => {
            const matchesSearchTerm = mobilisationSearchTerm === '' ||
                mob.title.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                mob.program.name.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                mob.city.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                mob.state.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                (user?.role === 'SUPER_ADMIN' && mob.mobilisation_partner?.name.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()));
            return matchesSearchTerm;
        });

        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    }, [traineeMobilisations, mobilisationSearchTerm, currentPage, itemsPerPage, user?.role]);

    const totalMobilisationPages = useMemo(() => {
        const filtered = traineeMobilisations.filter(mob => {
            const matchesSearchTerm = mobilisationSearchTerm === '' ||
                mob.title.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                mob.program.name.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                mob.city.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                mob.state.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()) ||
                (user?.role === 'SUPER_ADMIN' && mob.mobilisation_partner?.name.toLowerCase().includes(mobilisationSearchTerm.toLowerCase()));
            return matchesSearchTerm;
        });
        return Math.ceil(filtered.length / itemsPerPage);
    }, [traineeMobilisations, mobilisationSearchTerm, itemsPerPage, user?.role]);

    const handleMobilisationPageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
                    <p className="text-gray-700 text-lg">Loading trainee data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8 font-sans">
            {/* Notifications */}
            {error && (
                <div className="fixed inset-x-0 top-4 mx-auto w-full max-w-sm rounded-lg bg-red-50 p-4 shadow-lg flex items-center space-x-3 z-50">
                    <XCircle className="h-6 w-6 text-red-500" />
                    <span className="text-sm font-medium text-red-800">{error}</span>
                    <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}
            {success && (
                <div className="fixed inset-x-0 top-4 mx-auto w-full max-w-sm rounded-lg bg-green-50 p-4 shadow-lg flex items-center space-x-3 z-50">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <span className="text-sm font-medium text-green-800">{success}</span>
                    <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Main Content Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 lg:p-8">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6 border-b border-gray-200 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 leading-tight">Trainees Registration</h1>
                        <p className="mt-1 text-base text-gray-500">
                            Handle Trainees registration
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                            * Effective End Date is whichever comes first: registration end date (1 month from proposal approval) or Launchpad Session 1 day.
                        </p>
                    </div>
                </div>

                {/* Search, Upload, and Download Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search mobilisations by title, program, location, or partner..."
                            className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-blue-500"
                            value={mobilisationSearchTerm}
                            onChange={(e) => {
                                setMobilisationSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={downloadTemplate}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700"
                            disabled={downloadLoading}
                        >
                            {downloadLoading ? (
                                <span className="animate-spin mr-2 h-4 w-4 rounded-full border-2 border-gray-500 border-t-transparent"></span>
                            ) : (
                                <Download className="w-4 h-4 mr-2" />
                            )}
                            Download Template
                        </button>
                        {isMPAdminOrCAAdmin && (
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-colors hover:bg-blue-700 disabled:bg-blue-300"
                            >
                                <Upload className="mr-2 h-4 w-4" />
                                Upload Trainees
                            </button>
                        )}
                    </div>
                </div>

                {/* Mobilisation Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAndPaginatedMobilisations.length > 0 ? (
                        filteredAndPaginatedMobilisations.map(mob => (
                            <MobilisationCard key={mob.mobilisation_id} mobilisation={mob} onClick={setSelectedMobilisation} userRole={user?.role} />
                        ))
                    ) : (
                        <div className="md:col-span-2 lg:col-span-3 text-center py-10 text-gray-500 bg-gray-50 rounded-xl">
                            <p>No mobilisations found matching your search.</p>
                        </div>
                    )}
                </div>

                {/* Main list pagination controls */}
                {totalMobilisationPages > 1 && (
                    <div className="flex items-center justify-center space-x-4 mt-6">
                        <button
                            onClick={() => handleMobilisationPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">
                            Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalMobilisationPages}</span>
                        </span>
                        <button
                            onClick={() => handleMobilisationPageChange(currentPage + 1)}
                            disabled={currentPage === totalMobilisationPages}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Trainee Details Modal */}
            {selectedMobilisation && (
                <MobilisationDetailsModal
                    mobilisation={selectedMobilisation}
                    onClose={() => { setSelectedMobilisation(null); setSuccess(null); setError(null); }}
                />
            )}

            {/* Upload Trainees Modal */}
            {showUploadModal && (
                <UploadTraineesModal
                    onClose={() => setShowUploadModal(false)}
                    onSubmit={onSubmit}
                    openMobilisations={openMobilisations}
                    uploading={uploading}
                />
            )}
        </div>
    );
};

export default TraineesRegistration;
